import type { LayoutItem } from 'react-grid-layout';
import type { LayoutZone } from '@/persistence/config-db';
import type { WidgetKind } from '@/types/widget';

export type GridBreakpoint = 'lg' | 'md' | 'sm' | 'xs';

/** Breakpoint → min-width mapping (in px). */
export const GRID_BREAKPOINTS: Record<GridBreakpoint, number> = {
  lg: 1200,
  md: 768,
  sm: 480,
  xs: 0,
};

/** Columns for the KPI zone (4 per row on lg). */
export const KPI_COLS: Record<GridBreakpoint, number> = {
  lg: 4,
  md: 4,
  sm: 1,
  xs: 1,
};

/** Columns for the chart zone (2 per row on lg). */
export const CHART_COLS: Record<GridBreakpoint, number> = {
  lg: 2,
  md: 2,
  sm: 1,
  xs: 1,
};

/** Footprint of a KPI card. */
const KPI_W = 1;
const KPI_H = 2;

/** Footprint of a chart widget. */
const CHART_W = 1;
const CHART_H = 5;

/**
 * Footprint of a compare-table widget. They render winners + losers side
 * by side and need the full chart-zone width (both columns on `lg`).
 * Base height is small (just enough for the title bar + header row +
 * ~2 data rows); the actual height per widget grows with the user's
 * "Top N" limit via `compareTableExtraRows` so a table with limit=5
 * stays short and one with limit=50 grows tall instead of leaving a
 * sea of empty space below the data.
 */
const COMPARE_TABLE_W = 2;
const COMPARE_TABLE_H = 3;

type ZoneDims = {
  w: number;
  h: number;
  cols: number;
};

/**
 * Per-widget grid footprint within a zone. Chart-zone widgets are
 * normally `1 × 5`, but compare-table widgets get `2 × 10` so they
 * occupy the full row at twice the height (item 3).
 */
function zoneDims(zone: LayoutZone, kind?: WidgetKind): ZoneDims {
  if (zone === 'kpi') {
    return { w: KPI_W, h: KPI_H, cols: KPI_COLS.lg };
  }
  if (kind === 'compare-table') {
    return { w: COMPARE_TABLE_W, h: COMPARE_TABLE_H, cols: CHART_COLS.lg };
  }
  return { w: CHART_W, h: CHART_H, cols: CHART_COLS.lg };
}

/**
 * Extra grid rows needed for a compare-table widget that displays `rows`
 * data rows on its longest side (max of winners vs losers). Pairs with
 * `COMPARE_TABLE_H = 3`. Calibrated so the top-N cap of 10 lands on
 * 5 grid cells — enough to comfortably fit 10 rows + section / column
 * headers + widget chrome with a small visual margin.
 *
 * Callers pass the live rendered row count from `widget-runtime-store`
 * when available, falling back to `widget.limit` for the first paint
 * before the query has resolved.
 *
 * Math: with rowHeight=80 px + 6 px margin, 1 grid cell ≈ 86 px ≈
 * 3.5 table rows. `ceil((rows - 2) / 4)` extra cells gives the snug-
 * fit pattern: 0–2 rows stay on the 3-cell base, 3–6 rows bump to 4,
 * 7–10 rows land on 5 cells.
 */
export function compareTableExtraRows(rows: number | undefined): number {
  const n = rows ?? 10;
  if (n <= 2) return 0;
  return Math.ceil((n - 2) / 4);
}

/** Build a fixed-size, non-resizable layout item at the given position. */
function makeItem(
  id: string,
  x: number,
  y: number,
  dims: ZoneDims,
  extraH = 0,
): LayoutItem {
  const h = dims.h + extraH;
  return {
    i: id,
    x,
    y,
    w: dims.w,
    h,
    minW: dims.w,
    maxW: dims.w,
    minH: h,
    maxH: h,
    isResizable: false,
  };
}

/**
 * Compute the effective layout for a zone given:
 * - `widgetIds` — the current widget ids in the zone, in display order
 * - `persisted` — optional persisted layout from IndexedDB
 * - `extraRowsById` — per-widget extra rows (e.g. from multi-line notes)
 *
 * Known widgets keep their persisted position; unknown (newly-added)
 * widgets are appended below the persisted rows in a deterministic
 * left-to-right, top-to-bottom pattern. Stale persisted items whose
 * widget no longer exists are dropped. Every item's dims are clamped to
 * the current `zoneDims` + extraH, so older persisted heights (or stale
 * extraH from a previous note length) are rewritten on render.
 *
 * Extra rows stay **per widget** — a long note on one card grows that
 * card's cell only; shorter neighbours keep their base height and leave
 * empty space below their body. Row spacing still honours the tallest
 * card so the next row never overlaps it.
 */
export function computeZoneLayout(
  zone: LayoutZone,
  widgetIds: readonly string[],
  persisted: readonly LayoutItem[] | undefined,
  extraRowsById?: Readonly<Record<string, number>>,
  widgetKindById?: Readonly<Record<string, WidgetKind>>,
): LayoutItem[] {
  // Default zone dims — used for KPI zone and as the fallback when a
  // widget's kind isn't supplied (older callers haven't been updated).
  const defaultDims = zoneDims(zone);
  const dimsForId = (id: string): ZoneDims =>
    zoneDims(zone, widgetKindById?.[id]);

  const persistedMap = new Map(
    (persisted ?? []).map((item) => [item.i, item] as const),
  );

  const getExtra = (id: string): number => extraRowsById?.[id] ?? 0;

  type Placement = { id: string; x: number; y: number; extra: number };
  const placements: Placement[] = [];

  const missing: string[] = [];
  for (const id of widgetIds) {
    const p = persistedMap.get(id);
    if (p) {
      placements.push({ id, x: p.x, y: p.y, extra: getExtra(id) });
    } else {
      missing.push(id);
    }
  }

  // Append new widgets below the tallest kept widget so they never
  // overlap a neighbour whose note grew its cell.
  const baseY = placements.reduce((max, p) => {
    const d = dimsForId(p.id);
    return Math.max(max, p.y + d.h + p.extra);
  }, 0);

  // Stream new widgets left-to-right. Each widget consumes `dims.w`
  // columns of the current row; once the row overflows, advance to the
  // next row stepping by the tallest placement of the row that just
  // completed. This keeps the legacy 2-column chart pattern while
  // letting full-width compare-tables claim their own row cleanly.
  let yCursor = baseY;
  let colCursor = 0;
  let rowMaxH = 0;
  let rowMaxExtra = 0;
  for (const id of missing) {
    const d = dimsForId(id);
    const extra = getExtra(id);
    if (colCursor + d.w > defaultDims.cols) {
      yCursor += rowMaxH + rowMaxExtra;
      colCursor = 0;
      rowMaxH = 0;
      rowMaxExtra = 0;
    }
    placements.push({ id, x: colCursor, y: yCursor, extra });
    colCursor += d.w;
    if (d.h > rowMaxH) rowMaxH = d.h;
    if (extra > rowMaxExtra) rowMaxExtra = extra;
  }

  return placements.map((p) => makeItem(p.id, p.x, p.y, dimsForId(p.id), p.extra));
}
